import React from "react"
import styled from "styled-components"
import colors from "@constants/colors"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import {
  h4Styles,
  body16MainStyles,
  smallcapsBold13Styles,
  smallcapsBold14Styles,
  subtextStyles,
} from "@constants/typography"
import { Chevron, Close, Upload } from "@components/icons"
import Modal, { ModalBaseProps } from ".."
import IconButton, {
  Button as IconButtonContainer,
} from "@components/buttons/IconButton"
import TextButton from "@components/buttons/TextButton"
import TextInput from "@components/inputs/TextInput"
import StarsInput from "@components/StarsInput"
import useImageUploader from "@hooks/useImageUploader"
import { useReviewClient } from "../../../reviews/useReviewClient"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import { Formik, FormikHelpers as FormikActions } from "formik"
import { v4 as uuidv4 } from "uuid"
import * as Yup from "yup"

const Container = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media ${breakpoints.laptop} {
    flex-direction: row;
  }

  ${IconButtonContainer} {
    align-items: center;
    justify-content: center;
    display: flex;
    width: 100%;
    margin-top: 32px;

    @media ${breakpoints.laptop} {
      width: 248px;
      margin-top: 32px;
    }
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 72px;
  border-bottom: 1px solid ${colors.dividerLineGray};
  padding: 0 20px;

  @media ${breakpoints.laptop} {
    flex-direction: column;
    align-items: flex-start;
    width: calc(3 / 12 * 100vw);
    height: auto;
    border-bottom: none;
    padding: 0;
  }
`

const HeaderText = styled.div`
  ${h4Styles}
  color: ${colors.black};
  margin-left: 0;
`

const BackButton = styled.button`
  cursor: pointer;
  background: none;
  outline: none;
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;

  @media ${breakpoints.laptop} {
    margin-bottom: 22px;
  }
`

const BackButtonIcon = styled.div`
  height: 32px;
  width: 32px;
  transform: rotate(180deg);

  @media ${breakpoints.laptop} {
    height: 24px;
    width: 24px;
  }
`

const BackButtonText = styled.div`
  ${smallcapsBold13Styles}
  color: ${colors.black};
  margin-left: 8px;

  @media ${breakpoints.laptop} {
    ${body16MainStyles}
    color: ${colors.black};
    margin-left: 4px;
    text-transform: initial;
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  padding: 0 20px;

  @media ${breakpoints.laptop} {
    padding: 0;
  }
`

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
`

const Name = styled.div`
  width: 100%;
  margin-top: 25px;

  @media ${breakpoints.laptop} {
    width: calc((2 / 12 * 100vw) - 16px);
    margin-right: 16px;
    margin-top: 38px;
  }
`

const Email = styled.div`
  width: 100%;
  margin-top: 25px;

  @media ${breakpoints.laptop} {
    width: calc((2 / 12 * 100vw) - 16px);
    margin-left: 16px;
    margin-top: 38px;
  }
`

const Title = styled.div`
  width: 100%;
  margin-top: 25px;

  @media ${breakpoints.laptop} {
    width: calc(4 / 12 * 100vw);
    margin-top: 38px;
  }
`

const Body = styled.div`
  width: 100%;
  margin-top: 25px;

  @media ${breakpoints.laptop} {
    width: calc(4 / 12 * 100vw);
    margin-top: 38px;
    padding-top: 12px;
  }
`

const FormSubmit = styled(TextButton)`
  width: 100%;
  margin-top: 62px;
  padding-top: 4px;

  @media ${breakpoints.laptop} {
    width: 248px;
    margin-top: 58px;
  }
`

const RatingsInputWrapper = styled.div`
  margin-top: 25px;

  @media ${breakpoints.laptop} {
    margin-top: 0;
  }
`

const RatingsInput = styled(StarsInput)``

const RatingsInputText = styled.div`
  ${smallcapsBold13Styles}
  color: ${colors.black};
  font-size: 11px;
  line-height: 19px;
  padding: 8px 0;

  @media ${breakpoints.laptop} {
    padding: 16px 0;
  }
` as any

const UploadInput = styled.input`
  visibility: hidden;
`

const UploadIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  margin-right: 8px;
`

const UploadText = styled.div`
  ${smallcapsBold14Styles}
`

const UploadInfo = styled.div`
  ${subtextStyles}
  margin-top: 16px;
  color: ${colors.black};
`

const ImagePreviews = styled.div`
  align-items: flex-start;
  justify-content: flex-start;
  display: flex;
  margin: 32px 0;
`

const ImageWrapper = styled.div`
  position: relative;
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  width: 80px;
  height: 80px;
  margin-right: 8px;
`

export const ImageContainer = styled.div`
  position: relative;
  flex-grow: 0;
  flex-shrink: 0;
  width: 100%;
  height: auto;
  overflow: hidden;
  border-radius: 2px;
  border: solid 1px ${colors.black};
`

const ImagePadding = styled.div`
  width: 100%;
  padding-bottom: 100%;
`

const Image = styled.img`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
`

const RemoveButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  display: flex;
  opacity: 0;
  transform: opacity ${animation.medium} ${cubicBezier};
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 6px;
  outline: none;
  border: none;
  border-radius: 2px;
  background: ${colors.dividerLineGray};

  &:active {
    outline: none;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    opacity: 0.8;
    transform: opacity ${animation.quick} ${cubicBezier};
    cursor: pointer;

    svg {
      stroke: ${colors.offBlack};
    }
  }
`

interface WriteReviewModalProps extends ModalBaseProps {
  productId: string
  productTitle: string
  onSubmit: () => void
  backToReviews: () => void
}

const ReviewSchema = Yup.object().shape({
  rating: Yup.number().min(1).max(5),
  name: Yup.string().required("Your name is required"),
  email: Yup.string().email("Invalid email").required("Your email is required"),
  title: Yup.string().required("A review title is required"),
  body: Yup.string().required("A review is required"),
})

const WriteReviewModal = ({
  productId,
  productTitle,
  onSubmit,
  backToReviews,
  ...rest
}: WriteReviewModalProps) => {
  const uploadRef = React.useRef<HTMLInputElement>(null)

  const triggerInput = () => {
    if (uploadRef.current) {
      uploadRef.current.click()
    }
  }

  const { uploadFile } = useImageUploader()

  const [images, setImages] = React.useState<any>([])
  const imagesRef = React.useRef()
  imagesRef.current = images

  const [isUploadingImage, setIsUploadingImage] = React.useState(false)
  const [showMaxSizeWarning, setShowMaxSizeWarning] = React.useState(false)

  const removeImage = (id: string) => {
    const images = imagesRef.current || []
    setImages(images.filter((image: any) => image.id !== id))
  }

  const onSelectImages = async (event) => {
    if (!event.target.files) {
      return
    }

    const uploadedImages: any = []

    setIsUploadingImage(true)

    for (let i = 0; i < event.target.files.length; i++) {
      const file = event.target.files[i]
      const maxAllowedSize = 10 * 1024 * 1024

      try {
        if (file.size > maxAllowedSize) {
          if (!showMaxSizeWarning) {
            setShowMaxSizeWarning(true)
          }
        } else {
          const extSplit = file.name.split(".")
          const ext = extSplit[extSplit.length - 1]
          const fileName = ext ? `${uuidv4()}.${ext}` : uuidv4()
          const fileLocalUrl = URL.createObjectURL(file)

          const upload = await uploadFile({
            fileName,
            file: file,
          })

          uploadedImages.push({
            id: fileName,
            localUrl: fileLocalUrl,
            downloadUrl: upload,
            onRequestRemove: () => {
              removeImage(fileName)
            },
          })
        }
      } catch (error) {
        console.log(error)
      }
    }

    if (uploadedImages.length > 0) {
      setImages([...images, ...uploadedImages])
    }

    setIsUploadingImage(false)
  }

  React.useEffect(() => {
    if (isUploadingImage && showMaxSizeWarning) {
      setShowMaxSizeWarning(false)
    }
  }, [isUploadingImage])

  const { client } = useReviewClient()

  const onSubmitReview = async (
    values: {
      rating: number
      name: string
      email: string
      title: string
      body: string
    },
    formikActions: FormikActions<{
      rating: number
      name: string
      email: string
      title: string
      body: string
    }>
  ) => {
    try {
      const review: any = {
        sku: productId,
        product_title: productTitle,
        product_url: process.env.GATSBY_SHOPIFY_SHOP_DOMAIN ?? "",
        display_name: values.name,
        email: values.email,
        review_content: values.body,
        review_title: values.title,
        review_score: values.rating,
      }

      if (images.length > 0) {
        review.image_urls = images.map((image) => image.downloadUrl)
      }

      await client.createProductReview(review)

      onSubmit()
      formikActions.setSubmitting(false)
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Modal {...rest}>
      <Container>
        <Header>
          <BackButton onClick={() => backToReviews()}>
            <BackButtonIcon>
              <Chevron />
            </BackButtonIcon>
            <DesktopOnly>
              <BackButtonText>Back to all reviews</BackButtonText>
            </DesktopOnly>
            <MobileAndTabletOnly>
              <BackButtonText>Write a Review</BackButtonText>
            </MobileAndTabletOnly>
          </BackButton>
          <DesktopOnly>
            <HeaderText>Write a Review</HeaderText>
          </DesktopOnly>
        </Header>
        <Formik
          initialValues={{
            rating: 0,
            name: "",
            email: "",
            title: "",
            body: "",
          }}
          validationSchema={ReviewSchema}
          onSubmit={onSubmitReview}
          validateOnBlur={false}
          validateOnChange={false}
          render={({
            handleChange,
            handleBlur,
            handleSubmit,
            values,
            errors,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <RatingsInputWrapper>
                <RatingsInputText
                  css={errors.rating !== undefined ? `color: #f44336` : ``}
                >
                  Select Rating
                </RatingsInputText>
                <RatingsInput
                  stars={values.rating}
                  onChange={(stars) => {
                    setFieldValue("rating", stars)
                    setFieldTouched("rating")
                  }}
                />
              </RatingsInputWrapper>
              <FormRow>
                <Name>
                  <TextInput
                    type="text"
                    name="name"
                    label="Name"
                    placeholder="Name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    error={errors.name !== undefined}
                  />
                </Name>
                <Email>
                  <TextInput
                    type="email"
                    name="email"
                    label="Email"
                    placeholder="Email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    error={errors.email !== undefined}
                  />
                </Email>
              </FormRow>
              <FormRow>
                <Title>
                  <TextInput
                    type="text"
                    name="title"
                    label="Title of review"
                    placeholder="Title of review"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.title}
                    error={errors.title !== undefined}
                  />
                </Title>
              </FormRow>
              <FormRow>
                <Body>
                  <TextInput
                    type="text"
                    name="body"
                    label="Write your review"
                    placeholder="Write your review"
                    multiline={true}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.body}
                    error={errors.body !== undefined}
                  />
                </Body>
              </FormRow>
              <UploadInput
                ref={uploadRef}
                type="file"
                accept="image/*"
                multiple
                onChange={onSelectImages}
              />
              <IconButton
                type="button"
                inverted
                disabled={images.length === 5 || isUploadingImage}
                onClick={() => triggerInput()}
              >
                <UploadIconWrapper>
                  <Upload />
                </UploadIconWrapper>
                <UploadText>Upload Photos</UploadText>
              </IconButton>
              {isUploadingImage && (
                <UploadInfo>{"Uploading image(s)..."}</UploadInfo>
              )}
              {images.length === 5 && (
                <UploadInfo>Maximum of 5 images reached</UploadInfo>
              )}
              {showMaxSizeWarning && (
                <UploadInfo>Images must be less than 10mb</UploadInfo>
              )}
              <ImagePreviews>
                {images.map((image) => (
                  <ImageWrapper key={image.id}>
                    <ImageContainer>
                      <RemoveButton
                        type="button"
                        onClick={image.onRequestRemove}
                      >
                        <Close
                          strokeColor={colors.mediumDarkGray}
                          strokeWidth="2.5"
                        />
                      </RemoveButton>
                      <ImagePadding />
                      <Image src={image.localUrl} />
                    </ImageContainer>
                  </ImageWrapper>
                ))}
              </ImagePreviews>
              <DesktopOnly>
                <FormSubmit
                  type="submit"
                  text="Submit Review"
                  disabled={isSubmitting}
                />
              </DesktopOnly>
              <MobileAndTabletOnly>
                <FormSubmit
                  type="submit"
                  text="Submit"
                  disabled={isSubmitting}
                />
              </MobileAndTabletOnly>
            </Form>
          )}
        />
      </Container>
    </Modal>
  )
}

export default WriteReviewModal
